import { Component, computed, inject, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { IconNames } from '@icon/assets';
import { UserService } from '@shared';
import { UiButtonComponent, UIIconComponent } from '@design-system';
import { filter, map } from 'rxjs/operators';
import { isMainNavActive } from '../../models/main-nav.utils';
import { LogoutBottomSheetComponent } from '../../modules/menu/components/logout-bottom-sheet/logout-bottom-sheet.component';
import { MENU_ACCOUNT_ROUTE } from '../../modules/menu/menu.model';
import { AppUpdateService } from '../../services/app-update.service';
import { SIDE_MENU_ITEMS, SIDE_MENU_SETTINGS_ROUTE, SideMenuItem } from './side-menu.model';

/**
 * The desktop side menu — what the bottom tabs and the سایر page's profile card
 * are on a phone: the brand, the account (with a shortcut to تنظیمات), the five
 * destinations, خروج, and the build with a refresh that fetches the latest one.
 */
@Component({
	selector: 'app-side-menu',
	imports: [RouterLink, UIIconComponent, UiButtonComponent, LogoutBottomSheetComponent],
	templateUrl: './side-menu.component.html',
	host: {
		class: 'flex flex-col gap-4 rounded-xl bg-gray-000 px-4 pt-4'
	}
})
export class SideMenuComponent {
	private readonly router = inject(Router);
	private readonly userService = inject(UserService);
	private readonly appUpdate = inject(AppUpdateService);

	private readonly logoutSheet = viewChild<LogoutBottomSheetComponent>('logoutSheet');

	readonly items = SIDE_MENU_ITEMS;
	readonly accountRoute = MENU_ACCOUNT_ROUTE;
	readonly settingsRoute = SIDE_MENU_SETTINGS_ROUTE;

	readonly checkingForUpdate = this.appUpdate.checkingForUpdate;
	readonly versionLabel = this.appUpdate.versionLabel;

	readonly userName = computed(() => this.userService.userAccountInfoSignal()?.userFullName?.trim() || '');

	private readonly url = toSignal(
		this.router.events.pipe(
			filter((e): e is NavigationEnd => e instanceof NavigationEnd),
			map(e => e.urlAfterRedirects)
		),
		{ initialValue: this.router.url }
	);

	isActive(item: SideMenuItem): boolean {
		return isMainNavActive(this.url(), item.route);
	}

	iconOf(item: SideMenuItem): IconNames {
		return (this.isActive(item) ? `${item.icon}-fill` : item.icon) as IconNames;
	}

	onLogoutClick(): void {
		this.logoutSheet()?.open();
	}

	onRefreshClick(): void {
		this.appUpdate.reloadToLatest().subscribe();
	}
}
